import { mainClient } from '@/utils/api/mainClient';

export type OnboardingMode = 'allow_all' | 'invite_only' | 'personal_only' | 'organisation_only';

export type PendingInvite = {
  id: string;
  invite_code: string;
  invite_type: 'personal' | 'organisation';
  tenant_id: string | null;
  tenant_name: string | null;
  sender_name: string;
  role_id: string | null;
  created_at: string;
};

export type SentInvite = {
  id: string;
  receiver_email: string;
  invite_type: 'personal' | 'organisation';
  receiver_join_status: 'pending' | 'joined' | 'declined';
  role_id: string | null;
  role_name: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  created_at: string;
};

export type OrganisationRole = {
  role_id: string;
  role_name: string;
  role_code: string;
};

export type InviteValidation = {
  valid: boolean;
  invite_type: 'personal' | 'organisation';
  tenant_id: string | null;
  tenant_name: string | null;
  sender_name: string;
  receiver_email: string;
  receiver_exists: boolean;
};

// Returns platform onboarding mode — accessible without auth
export async function getOnboardingConfig(): Promise<OnboardingMode> {
  try {
    const { data } = await mainClient.get<{ mode: OnboardingMode }>('/api/invites/config');
    return data.mode ?? 'allow_all';
  } catch {
    return 'allow_all';
  }
}

// Returns pending invites for the authenticated user (by email, server-side lookup)
export async function getPendingInvites(): Promise<PendingInvite[]> {
  const { data } = await mainClient.get<{ invites: PendingInvite[] }>('/api/invites/pending');
  return data.invites ?? [];
}

// Validate an invite code (public — no auth required)
export async function validateInvite(code: string): Promise<InviteValidation> {
  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  const response = await fetch(`${apiUrl}/api/invites/validate/${code}`);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? 'Invalid invite');
  }
  return response.json();
}

// Accept or decline an invite
export async function respondToInvite(
  code: string,
  action: 'accept' | 'decline'
): Promise<{ success: boolean; tenant_id?: string; invite_type?: string }> {
  const { data } = await mainClient.post<{ success: boolean; tenant_id?: string; invite_type?: string }>(
    `/api/invites/${code}`,
    { action }
  );
  return data;
}

// Returns active roles for a given organisation (used when sending invites)
export async function getOrganisationRoles(tenantId: string): Promise<OrganisationRole[]> {
  try {
    const { data } = await mainClient.get<{ roles: OrganisationRole[] }>(`/api/invites/roles/${tenantId}`);
    return data.roles ?? [];
  } catch {
    return [];
  }
}

// Returns all invites sent by the authenticated user (all statuses)
export async function getSentInvites(): Promise<SentInvite[]> {
  const { data } = await mainClient.get<{ invites: SentInvite[] }>('/api/invites/sent');
  return data.invites ?? [];
}

// Returns pending sent invites for an organisation (org admins)
export async function getOrganisationSentInvites(tenantId: string): Promise<SentInvite[]> {
  const { data } = await mainClient.get<{ invites: SentInvite[] }>(`/api/invites/organisation/${tenantId}`);
  return data.invites ?? [];
}

// Cancel a pending invite
export async function cancelInvite(inviteId: string): Promise<void> {
  await mainClient.delete(`/api/invites/cancel/${inviteId}`);
}

// Generate an invite link (authenticated)
export async function generateInvite(payload: {
  receiver_email: string;
  invite_type: 'personal' | 'organisation';
  tenant_id?: string;
  role?: string;
  role_id?: string;
}): Promise<{ invite_url: string; invite_id: string }> {
  const { data } = await mainClient.post<{ invite_url: string; invite_id: string }>(
    '/api/invites/generate',
    payload
  );
  return data;
}
